import React, { useReducer, useEffect, useContext, useState, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import ErrorHandler from '../components/ErrorHandler';
import CartReducer from '../reducers/cart';
import { dec, enc } from '../components/utils';

const SessionContext = React.createContext();
const useSessionContext = () => useContext(SessionContext); // see gatsby-browser.js also

const SessionProvider = ({ children, location }) => {

    // MARK: raise a warning if storage not available
    const [xStorage, setXStorage] = useState(typeof localStorage !== `undefined` ? JSON.parse(localStorage.getItem('groovyParachuteSession')) : null);

    const initialConsent = xStorage ? xStorage.consent : null;
    const [consent, setConsent] = useState(initialConsent ? initialConsent : { checked: false, granted: false });

    const initialCart = xStorage ? xStorage.cart : [];
    const [cart, cartDispatch] = useReducer(CartReducer, initialCart); // see cart.cartReducer
    const [isOpenCart, setIsOpenCart] = useState(false);
    const [cartComplete, setCartComplete] = useState(false); // cartComplete = true in checkout-form
    const [cartMeta, setCartMeta] = useState(xStorage ? xStorage.cartMeta : null); // init in shopping-cart.AddToCart
    const [updatePending, setUpdatePending] = useState(true);
    const [checkoutInfo, setCheckoutInfo] = useState(xStorage ? xStorage.checkoutInfo : null); // init on address submit in address-form, update on chosenRate in checkout-form

    const testSiteUrl = 'http://localhost:8000';
    const siteUrl = process.env.GATSBY_ENV === 'production' ? 'https://groovyparachute.com' : testSiteUrl;

    const windowSize = typeof window !== "undefined" && window.screen?.width;
    const MOBILE_THRESHOLD = 1500; // keep in sync with css @media

    const [mobile] = useState(windowSize ? windowSize <= MOBILE_THRESHOLD : false);

    //
    // See product query
    // to understand structure.
    //
    const data = useStaticQuery(graphql`
        query {
                    allMdx(filter: {frontmatter: {contentType: {eq: "product"}}}) {
                        edges {
                          node {
                            frontmatter {
                              specsByVariant {
                                variant {
                                    countries {
                                        code
                                        name
                                        tz
                                        states {
                                            code
                                            name
                                            states {
                                                code
                                                name
                                            }
                                        }
                                        type
                                    }
                                }
                                specs {
                                    pricing {
                                        country
                                        currency
                                        decimals
                                    }
                                }
                              }
                            }
                          }
                        }
                      }
                  }  
    `)

    //
    // Currencies for products on sale.
    //
    const currencies = (() => {

        let currenciesAdded = [];
        let currencies = [];
        const codeNamer = new Intl.DisplayNames(['en'], { type: 'region' });

        data.allMdx.edges.forEach(edge => {
            edge.node.frontmatter.specsByVariant.forEach(specVar => {
                specVar.specs.forEach(spec => {
                    spec.pricing.forEach(p => {
                        if (!currenciesAdded.includes(p.currency)) {
                            const tz = specVar.variant.countries.find(country => country.code === p.country).tz;
                            currencies.push({
                                id: p.currency,
                                desc: codeNamer.of(p.country),
                                countryCode: p.country,
                                tz: tz,
                                currencyCode: p.currency,
                                decimals: p.decimals
                            });
                            currenciesAdded.push(p.currency);
                        }
                    });
                });
            });
        });

        return currencies;

    })();

    const currencyGuess = guessCurrency();

    const [currency, setCurrency] = useState(xStorage ? xStorage.currency : currencyGuess);

    //
    // All countries given the products on sale.
    //
    const countries = (() => {
        const countries = [];
        const countriesAdded = [];
        data.allMdx.edges.forEach(edge => {
            edge.node.frontmatter.specsByVariant.forEach(specVar => {
                specVar.variant.countries.forEach(country => {
                    if (country.type === 'country') {
                        if (!countriesAdded.includes(country.code)) {
                            countries.push({
                                code: country.code,
                                name: country.name,
                                states: country.states
                            });
                            countriesAdded.push(country.code);
                        }
                    } else if (country.type === 'region') {
                        country.states.forEach(state => {
                            if (!countriesAdded.includes(state.code)) {
                                countries.push({
                                    code: state.code,
                                    name: state.name,
                                    states: state.states
                                });
                                countriesAdded.push(state.code);
                            }
                        });
                    }
                });
            });
        });
        return countries;
    })();

    const initialLogin = xStorage ? xStorage.login : null;
    const [login, setLogin] = useState(initialLogin && { ...initialLogin, userId: dec(initialLogin.userId) });

    let prevLogin = usePrevious(login);

    function usePrevious(login) {
        const ref = useRef();
        useEffect(() => {
            ref.current = login;
        }, [login]);
        return ref.current;
    }

    const navs = { 'home': 'home', 'shop': 'shop', 'blog': 'blog', 'about-us': 'about', 'contact': 'contact' };
    const [productCollections, setProductCollections] = useState(['Romantic & Sexy', 'Sporty & Adventurous', 'Bold & Independent']);
    const [status, setStatus] = useState({ orderId: null, email: null, itemData: null, orderDate: null, emailUpdates: false });
    const [activeNav, setActiveNav] = useState(navs[location?.pathname === '/' ? 'home' : location?.pathname ? location?.pathname.replace(/\//g, '') : '']);
    const [showMenu, setShowMenu] = useState(false); // mobile

    useEffect(() => {
        setActiveNav(navs[location?.pathname === '/' ? 'home' : location?.pathname ? location.pathname.replace(/\//g, '') : '']);
    }, [setActiveNav, location]);

    //
    // Reset cart on payment.
    // Leave currency set for a next order.
    //
    useEffect(() => {
        if (cartComplete) {
            // console.log('session-provider RESET_CART .......... updatePending: ' + updatePending);
            setCartMeta(null);
            setCheckoutInfo(null);
        }
    }, [cartComplete, setCartMeta, setCheckoutInfo]);

    //
    // Session to storage.
    // Load the Tag Manager defaults based on
    // storage value.
    //
    useEffect(() => {
        const old = window.consent;
        window.consent = { loaded: true, granted: consent.granted };

        if (!old) { // only on client init
            window.dataLayer = window.dataLayer || [];
            window.gtag = function gtag() { window.dataLayer.push(arguments); }
            const gtag = window.gtag;

            const flag = consent.granted ? 'granted' : 'denied';

            console.log('SESSION-PROVIDER init consent &&&&&&&&&& -> ' + flag);

            gtag('consent', 'default', {
                'ad_user_data': flag,
                'ad_personalization': flag,
                'ad_storage': flag,
                'analytics_storage': flag,
                'wait_for_update': 500 // MARK: is this needed?
            });
            gtag('js', new Date());

            if (consent.granted) {
                console.log('SESSION-PROVIDER loading GTM script');

                var gtmScript = document.createElement('script');
                gtmScript.async = true;
                gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${process.env.GATSBY_GTM}`;

                var firstScript = document.getElementsByTagName('script')[0];
                firstScript.parentNode.insertBefore(gtmScript, firstScript);
            }

        }

        if ((!login && prevLogin) && window.consent?.granted) {
            console.log('LOGOFF +++++++++++ sending [user_logoff]');
            window.gtag('event', 'user_logoff', { logon_duration: Date.now() - new Date(prevLogin.loginStart) });
        }

        //
        // If hook triggered by an event from a storage update
        // don't save again; this avoids a circular.
        //
        if (!triggeredByStorage.current) {
            localStorage.setItem('groovyParachuteSession', JSON.stringify({ cartMeta: cartMeta, cart: cart, login: login && { ...login, userId: enc(login.userId) }, checkoutInfo: checkoutInfo, currency: currency, consent: consent }));
        } else {
            triggeredByStorage.current = false;
        }

    }, [cart, cartMeta, checkoutInfo, currency, login, consent]); // updates in shopping-cart, checkout-form, store-currency, login

    //
    // Only on cart update when the cart is open; i.e. not during the checkout flow.
    // Resets to false in checkout-form
    // after API acknowledges a draft order.
    //
    useEffect(() => {
        if (isOpenCart && !cart.length) { // all items removed
            setCartMeta(null);
            setCheckoutInfo(null); // MARK: expand this to close the payment intent; see Issue Log 16 Jan 2025
            setUpdatePending(false);
            return;
        }
        if (updatePending) return;
        if (isOpenCart) {
            setUpdatePending(true);
        }
    }, [cart, updatePending, setUpdatePending]);

    const triggeredByStorage = useRef(false); // MARK: does this work?

    //
    // Share updates in case of multi
    // tabs / windows open.
    // Listens for changes to storage from another
    // tab or window.
    //
    // Changes will trigger the storage hook
    // so check for true updates to prevent a circular call between multi browser tabs.
    // See "localStorage.setItem" in hook above.
    //
    useEffect(() => {

        //
        // Deep compare all saved objects.
        // Convert any stored strings to in-memory versions.
        //
        const needsUpdate = (newX) => {
            if (!newX) return true;

            const hasChanges = (newObj, storageObj) => {
                if ((newObj && !storageObj) || (!newObj && storageObj)) {
                    return true;
                } else if (!newObj && !storageObj) {
                    return false;
                }
                return JSON.stringify(newObj) !== JSON.stringify(storageObj);
            }

            const conv = (l) => {
                if (!l) return null;
                return { ...l, userId: dec(l.userId) }
            }

            let changes = false;

            if (hasChanges(cartMeta, newX.cartMeta)) {
                changes = true;
            } else if (hasChanges(cart, newX.cart)) {
                changes = true;
            } else if (hasChanges(login, conv(newX.login))) {
                changes = true;
            } else if (hasChanges(checkoutInfo, newX.checkoutInfo)) {
                changes = true;
            } else if (hasChanges(currency, newX.currency)) {
                changes = true;
            } else if (hasChanges(consent, newX.consent)) {
                changes = true;
            }

            return changes;
        }

        const updater = (e) => {
            const { key, newValue } = e; // newValue as saved by another window
            if (key === "groovyParachuteSession") {
                const newX = JSON.parse(newValue);
                if (needsUpdate(newX) && !triggeredByStorage.current) { // compare in-memory vals to storage
                    triggeredByStorage.current = true;
                    setXStorage(newX);
                    setConsent(newX.consent || { checked: false, granted: false });
                    setCartMeta(newX.cartMeta || null);
                    cartDispatch({
                        type: 'REFRESH_CART',
                        products: newX.cart || []
                    });
                    setCheckoutInfo(newX.checkoutInfo || null);
                    setCurrency(newX.currency || guessCurrency(data));
                    setLogin(newX.login && { ...newX.login, userId: dec(newX.login.userId) } || null);
                }
            }
        };
        window.addEventListener("storage", updater); // get updates by other window
        return () => {
            window.removeEventListener("storage", updater);
        };
    }, []);

    //
    // Do a best guess of client currency
    // using Intl.DateTimeFormat()
    // which user can override via store-currency.
    // See https://stackoverflow.com/questions/38399465/how-to-get-list-of-all-timezones-in-javascript.
    //
    function guessCurrency() {

        const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone; // Europe/Berlin, Africa/Johannesburg, America/Indiana/Petersburg

        //
        // Will be wrong if landing
        // via a VPN.
        // Use JSON.stringify(Intl.supportedValuesOf('timeZone'))) to get all TZs.
        // A clientTz may be America/Indiana/Petersburg or America/Cambridge_Bay so use last
        // array entry to get city.
        //
        const guess = currencies.find(currency => {
            const comps = currency.tz.split('/'); // [Europe, Amsterdam,Andorra,Athens...]
            const region = comps[0];
            const cities = comps[1]?.split(','); // [Amsterdam,Andorra,Athens...]
            return cities?.length ? region === clientTz.split('/')[0] && cities.includes(clientTz.split('/').pop()) : region === clientTz.split('/')[0];
        });

        return guess ? guess : { id: 'USD', desc: 'United States', tz: 'America/New_York', countryCode: 'US', currencyCode: 'USD' };

    }

    const [currencyCountries, setCurrencyCountries] = useState();

    //
    // Respond to change in currency
    // to update countries available; either a single country, or a region
    // like the EU.
    //
    useEffect(() => {
        const countriesAdded = [];
        const countries = [];
        data.allMdx.edges.forEach(edge => {
            edge.node.frontmatter.specsByVariant.forEach(specVar => {
                specVar.variant.countries.forEach(country => {
                    if (country.code === currency.countryCode) {
                        if (country.type === 'country') {
                            if (!countriesAdded.includes(country.code)) {
                                countries.push({
                                    code: country.code,
                                    name: country.name,
                                    states: country.states
                                });
                                countriesAdded.push(country.code);
                            }
                        } else if (country.type === 'region') {
                            country.states.forEach(state => {
                                if (!countriesAdded.includes(state.code)) {
                                    countries.push({
                                        code: state.code,
                                        name: state.name,
                                        states: state.states
                                    });
                                    countriesAdded.push(state.code);
                                }
                            });
                        }
                    }
                });
            });
        });
        setCurrencyCountries(countries);
    }, [currency]);

    return (
        <SessionContext.Provider value={{
            cart, cartDispatch, isOpenCart, setIsOpenCart, cartMeta,
            setCartMeta, cartComplete, setCartComplete, updatePending, setUpdatePending,
            checkoutInfo, setCheckoutInfo, currencies, currency, setCurrency, currencyCountries, countries, login, setLogin, consent, setConsent,
            productCollections, setProductCollections, status, setStatus, activeNav, setActiveNav, showMenu, setShowMenu, siteUrl, mobile
        }}>
            <ErrorHandler siteUrl={siteUrl} userId={login?.userId}>
                {children}
            </ErrorHandler>
        </SessionContext.Provider>
    )

}

export { SessionProvider, useSessionContext }